import { useState, useEffect } from 'react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Plus, Trash2, ExternalLink } from 'lucide-react';
import { apiFetch } from '../utils/api';
import { formatAmount } from '../utils/formatters';
import { TagSelector } from './TagSelector';
import { useI18n } from '@/hooks/useI18n';

interface Employee {
    id: string;
    first_name: string;
    last_name: string;
    tax_id?: string;
}

interface CostCenter {
    id: string;
    name: string;
}

interface Contract {
    id: string;
    employee_id: string;
    default_cost_center_id: string;
}

interface Tag {
    id: string;
    name: string;
    color?: string;
    icon?: string;
}

interface PaymentRow {
    id?: string;  // people_costs record ID (undefined for new rows)
    employee_id: string;
    cost_center_id: string;
    amount: string;
    tags: Tag[];
    payment_transaction_id?: string;
}

interface SalaryBatchModalProps {
    isOpen: boolean;
    onClose: () => void;
    onSave: () => void;
    batchId?: string;
}

export function SalaryBatchModal({ isOpen, onClose, onSave, batchId }: SalaryBatchModalProps) {
    const { t } = useI18n();
    const [year, setYear] = useState<number>(new Date().getFullYear());
    const [month, setMonth] = useState<number>(new Date().getMonth() + 1);
    const [week, setWeek] = useState<number | null>(null);
    const [paymentType, setPaymentType] = useState<'monthly' | 'weekly'>('monthly');
    const [paymentDeadline, setPaymentDeadline] = useState<string>('');
    const [notes, setNotes] = useState<string>('');
    const [payments, setPayments] = useState<PaymentRow[]>([]);

    const [employees, setEmployees] = useState<Employee[]>([]);
    const [contracts, setContracts] = useState<Contract[]>([]);
    const [costCenters, setCostCenters] = useState<CostCenter[]>([]);
    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [baseCurrency, setBaseCurrency] = useState<string>('HUF');

    useEffect(() => {
        if (isOpen) {
            fetchUserData();
            fetchEmployees();
            fetchContracts();
            fetchCostCenters();

            if (batchId) {
                fetchBatchData();
            } else {
                const today = new Date();
                setYear(today.getFullYear());
                setMonth(today.getMonth() + 1);
                setWeek(null);
                setPaymentType('monthly');
                setPaymentDeadline(today.toISOString().split('T')[0]);
                setNotes('');
                setPayments([]);
            }
        }
    }, [isOpen, batchId]);

    const fetchUserData = async () => {
        try {
            const response = await apiFetch('/api/auth/me');
            if (response.ok) {
                const data = await response.json();
                setBaseCurrency(data.base_currency || 'HUF');
            }
        } catch (error) {
            console.error('Error fetching user data:', error);
        }
    };

    const fetchEmployees = async () => {
        try {
            const response = await apiFetch('/api/people/employees/?limit=100&is_active=true');
            if (response.ok) {
                const data = await response.json();
                setEmployees(data.employees || []);
            }
        } catch (error) {
            console.error('Error fetching employees:', error);
        }
    };

    const fetchContracts = async () => {
        try {
            const response = await apiFetch('/api/people/contracts/?limit=100&is_active=true');
            if (response.ok) {
                const data = await response.json();
                setContracts(data.contracts || []);
            }
        } catch (error) {
            console.error('Error fetching contracts:', error);
        }
    };

    const fetchCostCenters = async () => {
        try {
            const response = await apiFetch('/api/cost-centers/?limit=100');
            if (response.ok) {
                const data = await response.json();
                setCostCenters(Array.isArray(data) ? data : []);
            }
        } catch (error) {
            console.error('Error fetching cost centers:', error);
        }
    };

    const fetchBatchData = async () => {
        if (!batchId) return;

        setLoading(true);
        try {
            const response = await apiFetch(`/api/people/salary-batches/${batchId}`);
            if (response.ok) {
                const data = await response.json();
                setYear(data.year);
                setMonth(data.month);
                setWeek(data.week || null);
                setPaymentType(data.week ? 'weekly' : 'monthly');
                setPaymentDeadline(data.payment_deadline);
                setNotes(data.notes || '');

                const paymentRows = data.payments.map((p: any) => ({
                    id: p.id,  // Store the people_costs record ID
                    employee_id: p.employee_id,
                    cost_center_id: p.cost_center_id,
                    amount: p.amount?.toString() || '0',
                    tags: p.tags || [],
                    payment_transaction_id: p.payment_transaction_id
                }));
                setPayments(paymentRows);
            }
        } catch (error) {
            console.error('Error fetching batch data:', error);
        } finally {
            setLoading(false);
        }
    };

    const addPaymentRow = () => {
        setPayments([...payments, {
            id: `temp-${Date.now()}-${Math.random()}`, // Temporary client-side ID
            employee_id: '',
            cost_center_id: '',
            amount: '0',
            tags: []
        }]);
    };

    const removePaymentRow = (index: number) => {
        const row = payments[index];
        if (row.payment_transaction_id) {
            alert('Cannot remove payment with linked transaction');
            return;
        }
        setPayments(payments.filter((_, i) => i !== index));
    };

    const updatePaymentRow = (index: number, field: keyof PaymentRow, value: any) => {
        const newPayments = [...payments];
        newPayments[index] = { ...newPayments[index], [field]: value };
        setPayments(newPayments);
    };

    const getEmployeeCostCenters = (employeeId: string): CostCenter[] => {
        const employeeContracts = contracts.filter(c => c.employee_id === employeeId);
        const costCenterIds = new Set(employeeContracts.map(c => c.default_cost_center_id).filter(Boolean));
        return costCenters.filter(cc => costCenterIds.has(cc.id));
    };

    const getEmployeeById = (id: string): Employee | undefined => {
        return employees.find(e => e.id === id);
    };

    const calculateTotal = (): number => {
        return payments.reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0);
    };

    const handleSave = async () => {
        if (!paymentDeadline) {
            alert('Please select a payment deadline');
            return;
        }

        if (paymentType === 'weekly' && !week) {
            alert('Please select a week number for weekly payments');
            return;
        }

        if (payments.length === 0) {
            alert('Please add at least one payment');
            return;
        }

        for (const payment of payments) {
            if (!payment.employee_id || !payment.cost_center_id) {
                alert('Please fill in all employee and cost center fields');
                return;
            }
            if (!payment.amount || parseFloat(payment.amount) <= 0) {
                alert('Please enter valid amounts for all payments');
                return;
            }
        }

        setSaving(true);
        try {
            const payload = {
                year,
                month,
                week: paymentType === 'weekly' ? week : null,
                payment_deadline: paymentDeadline,
                notes: notes || null,
                payments: payments.map(p => ({
                    employee_id: p.employee_id,
                    cost_center_id: p.cost_center_id,
                    amount: parseFloat(p.amount),
                    tag_ids: p.tags.map(t => t.id)
                }))
            };

            const url = batchId
                ? `/api/people/salary-batches/${batchId}`
                : '/api/people/salary-batches/';

            const method = batchId ? 'PUT' : 'POST';

            const response = await apiFetch(url, {
                method,
                headers: { 'Content-Type': 'application/json' },
                body: JSON.stringify(payload)
            });

            if (response.ok) {
                const savedBatch = await response.json();

                // Update local state with real IDs from server
                if (savedBatch.payments && savedBatch.payments.length > 0) {
                    const updatedPayments = payments.map((localPayment, index) => {
                        const serverPayment = savedBatch.payments[index];
                        if (serverPayment) {
                            return {
                                ...localPayment,
                                id: serverPayment.id, // Replace temp ID with real ID
                                tags: serverPayment.tags || localPayment.tags
                            };
                        }
                        return localPayment;
                    });
                    setPayments(updatedPayments);
                }

                onSave();
                onClose();
            } else {
                const error = await response.json();
                alert(`Error: ${error.detail || 'Failed to save batch'}`);
            }
        } catch (error) {
            console.error('Error saving batch:', error);
            alert('Failed to save batch');
        } finally {
            setSaving(false);
        }
    };

    const totalAmount = calculateTotal();

    return (
        <Dialog open={isOpen} onOpenChange={onClose}>
            <DialogContent className="max-w-5xl max-h-[90vh] overflow-scroll ">
                <DialogHeader>
                    <DialogTitle className="text-2xl">{t('employees.costs.subtitle', 'common')}</DialogTitle>
                </DialogHeader>

                {loading ? (
                    <div className="py-8 text-center">{t('actions.loading', 'common')}</div>
                ) : (
                    <div className="space-y-6">
                        <div className="grid grid-cols-4 gap-2">
                            <div>
                                <Label>{t('employees.costs.paymentType', 'common')} *</Label>
                                <Select
                                    value={paymentType}
                                    onValueChange={(value: 'monthly' | 'weekly') => {
                                        setPaymentType(value);
                                        if (value === 'monthly') {
                                            setWeek(null);
                                        } else {
                                            // Set to current week when switching to weekly
                                            const now = new Date();
                                            const startOfYear = new Date(now.getFullYear(), 0, 1);
                                            const days = Math.floor((now.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
                                            const currentWeek = Math.ceil((days + startOfYear.getDay() + 1) / 7);
                                            setWeek(currentWeek);
                                        }
                                    }}
                                    disabled={!!batchId}
                                >
                                    <SelectTrigger>
                                        <SelectValue />
                                    </SelectTrigger>
                                    <SelectContent>
                                        <SelectItem value="monthly">{t('employees.costs.monthly', 'common')}</SelectItem>
                                        <SelectItem value="weekly">{t('employees.costs.weekly', 'common')}</SelectItem>
                                    </SelectContent>
                                </Select>
                            </div>

                            {paymentType === 'monthly' ? (
                                <div>
                                    <Label>{t('employees.costs.month', 'common')} *</Label>
                                    <Input
                                        type="month"
                                        value={`${year}-${month.toString().padStart(2, '0')}`}
                                        onChange={(e) => {
                                            const [y, m] = e.target.value.split('-');
                                            setYear(parseInt(y));
                                            setMonth(parseInt(m));
                                        }}
                                        disabled={!!batchId}
                                    />
                                </div>
                            ) : (
                                <>
                                    <div>
                                        <Label>{t('employees.costs.year', 'common')} *</Label>
                                        <Input
                                            type="number"
                                            min="2020"
                                            max="2030"
                                            value={year}
                                            onChange={(e) => setYear(parseInt(e.target.value))}
                                            disabled={!!batchId}
                                        />
                                    </div>
                                    <div>
                                        <Label>{t('employees.costs.week', 'common')} *</Label>
                                        <Input
                                            type="number"
                                            min="1"
                                            max="53"
                                            value={week || ''}
                                            onChange={(e) => {
                                                const weekNum = parseInt(e.target.value) || null;
                                                setWeek(weekNum);
                                                // Auto-calculate month from week for backend compatibility
                                                if (weekNum) {
                                                    // Approximate month from week (week 1-4 = Jan, 5-8 = Feb, etc.)
                                                    const approxMonth = Math.min(12, Math.ceil(weekNum / 4.33));
                                                    setMonth(approxMonth);
                                                }
                                            }}
                                            disabled={!!batchId}
                                            placeholder="1-53"
                                        />
                                    </div>
                                </>
                            )}

                            <div>
                                <Label>{t('employees.costs.paymentDeadline', 'common')} *</Label>
                                <Input
                                    type="date"
                                    value={paymentDeadline}
                                    onChange={(e) => setPaymentDeadline(e.target.value)}
                                />
                            </div>
                        </div>

                        <div className="grid grid-cols-2 gap-4">

                        </div>

                        <div>
                            <Label className="text-lg font-semibold mb-2 block">{t('employees.costs.amount', 'common')}</Label>
                            <div className="border rounded-lg overflow-hidden">
                                <table className="w-full">
                                    <thead className="bg-muted">
                                        <tr>
                                            <th className="p-2 text-left text-sm font-medium">{t('common.name', 'common')}</th>
                                            <th className="p-2 text-left text-sm font-medium">{t('common.taxid', 'common')}</th>
                                            <th className="p-2 text-left text-sm font-medium">{t('common.costCenter', 'common')}</th>
                                            <th className="p-2 text-left text-sm font-medium">{t('tags.tags', 'common')}</th>
                                            <th className="p-2 text-right text-sm font-medium">{t('employees.costs.gross', 'common')} ({baseCurrency})</th>
                                            <th className="p-2 text-right text-sm font-medium">{t('employees.costs.net', 'common')} ({baseCurrency})</th>
                                            <th className="p-2 w-10"></th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {payments.map((payment, index) => {
                                            const employee = getEmployeeById(payment.employee_id);
                                            const employeeCostCenters = payment.employee_id ? getEmployeeCostCenters(payment.employee_id) : [];
                                            const isLocked = !!payment.payment_transaction_id;

                                            return (
                                                <tr key={index} className="border-t">
                                                    <td className="p-2">
                                                        <Select
                                                            value={payment.employee_id}
                                                            onValueChange={async (value) => {
                                                                const centers = getEmployeeCostCenters(value);

                                                                // Fetch employee's active contract to get monthly_salary
                                                                try {
                                                                    const contractResponse = await apiFetch(`/api/people/contracts/?employee_id=${value}&is_active=true`);
                                                                    if (contractResponse.ok) {
                                                                        const contractData = await contractResponse.json();
                                                                        const contracts = contractData.contracts || [];

                                                                        // Find Full Time contract with monthly_salary
                                                                        const fullTimeContract = contracts.find((c: any) =>
                                                                            c.taxation_type === 'full_time_employee' && c.monthly_salary
                                                                        );

                                                                        const newPayments = [...payments];
                                                                        newPayments[index] = {
                                                                            ...newPayments[index],
                                                                            employee_id: value,
                                                                            cost_center_id: centers.length === 1 ? centers[0].id : '',
                                                                            amount: fullTimeContract?.monthly_salary?.toString() || newPayments[index].amount
                                                                        };
                                                                        setPayments(newPayments);
                                                                    }
                                                                } catch (error) {
                                                                    console.error('Error fetching contract:', error);
                                                                    // Fallback: just set employee and cost center
                                                                    const newPayments = [...payments];
                                                                    newPayments[index] = {
                                                                        ...newPayments[index],
                                                                        employee_id: value,
                                                                        cost_center_id: centers.length === 1 ? centers[0].id : ''
                                                                    };
                                                                    setPayments(newPayments);
                                                                }
                                                            }}
                                                        >
                                                            <SelectTrigger className="w-full">
                                                                <SelectValue placeholder="Válassz dolgozót" />
                                                            </SelectTrigger>
                                                            <SelectContent>
                                                                {employees.map(emp => (
                                                                    <SelectItem key={emp.id} value={emp.id}>
                                                                        {emp.first_name} {emp.last_name}
                                                                    </SelectItem>
                                                                ))}
                                                            </SelectContent>
                                                        </Select>
                                                    </td>
                                                    <td className="p-2 text-sm text-muted-foreground">
                                                        {employee?.tax_id || '-'}
                                                    </td>
                                                    <td className="p-2">
                                                        {employeeCostCenters.length === 0 ? (
                                                            <span className="text-sm text-muted-foreground">-</span>
                                                        ) : employeeCostCenters.length === 1 ? (
                                                            <span className="text-sm">
                                                                {employeeCostCenters[0].name}
                                                            </span>
                                                        ) : (
                                                            <Select
                                                                value={payment.cost_center_id}
                                                                onValueChange={(value) => updatePaymentRow(index, 'cost_center_id', value)}
                                                            >
                                                                <SelectTrigger className="w-full">
                                                                    <SelectValue placeholder="Költséghely" />
                                                                </SelectTrigger>
                                                                <SelectContent>
                                                                    {employeeCostCenters.map(cc => (
                                                                        <SelectItem key={cc.id} value={cc.id}>
                                                                            {cc.name}
                                                                        </SelectItem>
                                                                    ))}
                                                                </SelectContent>
                                                            </Select>
                                                        )}
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="w-full z-100">
                                                            <TagSelector
                                                                entityType="people_costs"
                                                                entityId={payment.id || ''}
                                                                selectedTags={payment.tags}
                                                                onChange={(tags) => updatePaymentRow(index, 'tags', tags)}
                                                                allowCreate={true}
                                                                localOnly={!payment.id || payment.id.startsWith('temp-')}
                                                            />
                                                        </div>
                                                    </td>
                                                    <td className="p-2">
                                                        <div className="flex items-center gap-2 justify-end">
                                                            <Input
                                                                type="number"
                                                                value={payment.amount}
                                                                onChange={(e) => updatePaymentRow(index, 'amount', e.target.value)}
                                                                disabled={isLocked}
                                                                className="text-right w-32"
                                                            />
                                                            {isLocked && (
                                                                <ExternalLink className="w-4 h-4 text-blue-500 cursor-pointer" />
                                                            )}
                                                        </div>
                                                    </td>
                                                    <td className="p-2 text-right text-sm text-muted-foreground">
                                                        {parseFloat(payment.amount) > 0
                                                            ? formatAmount(parseFloat(payment.amount) * 0.665, { currencyCode: baseCurrency })
                                                            : '-'
                                                        }
                                                    </td>
                                                    <td className="p-2">
                                                        <Button
                                                            variant="ghost"
                                                            size="sm"
                                                            onClick={() => removePaymentRow(index)}
                                                            disabled={isLocked}
                                                        >
                                                            <Trash2 className="w-4 h-4" />
                                                        </Button>
                                                    </td>
                                                </tr>
                                            );
                                        })}
                                    </tbody>
                                </table>
                            </div>

                            <Button
                                variant="outline"
                                size="sm"
                                onClick={addPaymentRow}
                                className="mt-2"
                            >
                                <Plus className="w-4 h-4 mr-2" />
                                {t('employees.costs.newRow', 'common')}
                            </Button>
                        </div>

                        <div className="flex justify-end">
                            <div className="text-lg font-semibold">
                                {t('employees.costs.totalAmount', 'common')} {formatAmount(totalAmount, { currencyCode: baseCurrency })}
                            </div>
                        </div>

                    </div>
                )}

                <div className="flex justify-end gap-2 mt-6 pt-4 border-t">
                    <Button variant="outline" onClick={onClose} disabled={saving}>
                        {t('actions.cancel', 'common')}
                    </Button>
                    <Button onClick={handleSave} disabled={saving || payments.length === 0}>
                        {saving ? t('actions.saving', 'common') : batchId ? t('employees.costs.updateBatch', 'common') : t('employees.costs.createBatch', 'common')}
                    </Button>
                </div>
            </DialogContent>
        </Dialog>
    );
}
